package net.sergeych.kilogin

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import net.sergeych.bintools.ByteChunk
import net.sergeych.crypto2.KDF
import net.sergeych.crypto2.VerifyingPublicKey
import net.sergeych.kiloparsec.command
import net.sergeych.tools.bipack


@Serializable
data class RegistrationArgs(
    val nonce: UByteArray,
    val loginNameKey: ByteChunk,
    val loginId: ByteChunk,
    val loginPublicKey: VerifyingPublicKey,
    val derivationParams: KDF,
    val restoreId: ByteChunk,
    val packedACO: UByteArray,
    val packedApplicationData: UByteArray? = null,
) {
    @Suppress("unused")

    inline fun <reified T> toSuccess(extraData: T): AuthenticationResult.Success {
        return AuthenticationResult.Success(
            loginNameKey, bipack(extraData)
        )
    }

    @Suppress("unused")
    inline fun <reified T> decodeOrNull(): T? = runCatching {
        packedApplicationData?.decodeBipack<T>()
    }.getOrNull()

    @Suppress("unused")
    inline fun <reified T : Any> decodeOrThrow(): T = packedApplicationData?.decodeBipack<T>()
        ?: throw IllegalArgumentException("missing require extra data of type ${T::class.simpleName}")
}

inline fun <reified T> UByteArray.decodeBipack(): T = biDecode(this)

@Serializable
sealed class AuthenticationResult {
    @Serializable
    @SerialName("Success")
    data class Success(val loginNameKey: ByteChunk, val extraData: UByteArray) :
        AuthenticationResult() {
        companion object {
            /**
             * Create success instance using either provided packed data or
             * by serializing it.
             *
             * Data of type `ByteArray` or `UByteArray` is treated as is, while other types are
             * serialized using [biEncode].
             */
            inline operator fun <reified T> invoke(
                loginNameKey: ByteChunk, data: T
            ) =
                Success(loginNameKey, when(data) {
                    is UByteArray -> data
                    is ByteArray -> data.toUByteArray()
                    else -> biEncode(data)
                })
        }
    }

    /**
     * Error condition representing that connection is already in logged in state, therefore can't request
     * authentication before logging out.
     * it is an error
     */
    @Serializable
    @SerialName("ConnectionLoggedIn")
    object ConnectionIsLoggedIn: AuthenticationResult()

    /**
     * Error condition when there is no suitable login for the specified credentials. Meaning depends on the
     * context: missing connectionId, wrong login/password combination, etc.
     */
    @Serializable
    @SerialName("LoginUnavailable")
    object LoginUnavailable : AuthenticationResult()

    @Serializable
    @SerialName("OtherError")
    class OtherError(val reason: String, val packedData: UByteArray? = null) : AuthenticationResult()


    @Serializable
    @SerialName("LoginIdUnavailable")
    object LoginIdUnavailable : AuthenticationResult()

    /**
     * Any other condition that require client to re-generate ACO, with new login and restore key, it could
     * be compromised or too weak key, whatever.
     */
    @Serializable
    @SerialName("RestoreIdUnavailable")
    object RestoreIdUnavailable : AuthenticationResult()
}

@Serializable
class RequestACOByLoginNameArgs(
    val loginNameKey: ByteChunk,
    val loginId: ByteChunk,
)

@Serializable
class RequestACOResult(
    val packedACO: UByteArray,
)

@Serializable
class LoginByPasswordPayload(
    val loginNameKey: ByteChunk,
    val nonce: UByteArray
)

@Serializable
class ChangePasswordArgs(
    val loginNameKey: ByteChunk,
    val packedSignedRecord: UByteArray,
)

@Serializable
class ChangePasswordPayload(
    val packedACO: UByteArray,
    val passwordDerivationParams: KDF,
    val newLoginKey: VerifyingPublicKey,
    val newLoginId: ByteChunk,
    val nonce: UByteArray
)


//val slGetNonce by command<Unit, UByteArray>()
val slRegister by command<UByteArray, AuthenticationResult>()
val slLogout by command<Unit, Unit>()
val slLoginByConnectionId by command<Unit, AuthenticationResult>()

val slRequestDerivationParams by command<ByteChunk, KDF>()
val slRequestACOByLoginName by command<RequestACOByLoginNameArgs, RequestACOResult>()
val slLoginByKey by command<UByteArray, AuthenticationResult>()

val slRequestACOBySecretId by command<ByteChunk, UByteArray>()
val slChangePasswordAndLogin by command<ChangePasswordArgs, AuthenticationResult>()

val slSendTestException by command<Unit, Unit>()
val slIsLoginNameAvailable by command<ByteChunk,Boolean>()

// client-side commands
val slLoginStateChanged by command<AuthenticationResult, Unit>()